import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Alert from '@mui/material/Alert';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import { ArrowLeft, CheckCircle, Copy } from 'lucide-react';
import { collection, addDoc } from 'firebase/firestore';
import { firestore } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { Role } from '../../../../shared/types/user.types';
import { SettingListItem } from '../../../../shared/types/setting.types';
import useSettingLists from '../../wrappers/use-setting-list-item';
import { useTranslation } from 'react-i18next';
import {
    createAccountStyles,
    invitationSuccessIconColor,
} from './CreateAccount.styles';

const CreateAccount: React.FC = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { t } = useTranslation();
    const settingLists = useSettingLists();
    const [dealers, setDealers] = useState<SettingListItem[]>([]);
    const [loadingDealers, setLoadingDealers] = useState(true);
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        role: 'dealer' as Role,
        dealerId: '',
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [invitationLink, setInvitationLink] = useState<string | null>(null);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        settingLists.list('dealers')
            .then((items) => setDealers(items.filter((i) => !i.blocked)))
            .finally(() => setLoadingDealers(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const dealerRequiresSelection = formData.role === 'dealer' && !formData.dealerId;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (dealerRequiresSelection) return;
        setLoading(true);
        setError(null);

        try {
            const token = crypto.randomUUID();
            const now = new Date();
            const expiresAt = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

            await addDoc(collection(firestore, 'user'), {
                email: formData.email,
                firstName: formData.firstName,
                lastName: formData.lastName,
                name: `${formData.firstName} ${formData.lastName}`.trim(),
                role: formData.role,
                ...(formData.role === 'dealer' && { dealerId: formData.dealerId }),
                token,
                status: 'pending',
                statusAt: now.toISOString(),
                expiresAt: expiresAt.toISOString(),
                createdBy: user?.uid ?? '',
            });

            setInvitationLink(`${window.location.origin}/register?token=${token}`);
        } catch (err) {
            setError(t('createAccount.error'));
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleCopy = async () => {
        if (!invitationLink) return;
        await navigator.clipboard.writeText(invitationLink);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const roleInfo: Partial<Record<Role, string>> = {
        admin: t('createAccount.roleInfoAdmin'),
        dealer: t('createAccount.roleInfoDealer'),
        customer: t('createAccount.roleInfoCustomer'),
    };

    if (invitationLink) {
        return (
            <Box sx={createAccountStyles.root}>
                <Box sx={createAccountStyles.titleRow}>
                    <IconButton component={Link} to="/app/admin/users" size="small">
                        <ArrowLeft size={20} />
                    </IconButton>
                    <Typography variant="h4" fontWeight={700}>{t('createAccount.title')}</Typography>
                </Box>

                <Paper elevation={0} sx={createAccountStyles.paper}>
                    <Box sx={createAccountStyles.successContent}>
                        <CheckCircle size={48} color={invitationSuccessIconColor} />
                        <Typography variant="h6">{t('createAccount.successTitle')}</Typography>
                        <Typography color="text.secondary">
                            {t('createAccount.successDescription', { name: `${formData.firstName} ${formData.lastName}`.trim(), email: formData.email })}
                        </Typography>

                        <Alert severity="info" sx={createAccountStyles.infoAlert}>
                            {t('createAccount.emailNotAvailableAlert')}
                        </Alert>

                        <Box sx={createAccountStyles.invitationBox}>
                            <Typography
                                variant="body2"
                                sx={createAccountStyles.invitationText}
                            >
                                {invitationLink}
                            </Typography>
                            <IconButton size="small" onClick={handleCopy} aria-label={t('createAccount.copyLinkTooltip')} title={t('createAccount.copyLinkTooltip')}>
                                <Copy size={16} />
                            </IconButton>
                        </Box>
                        {copied && (
                            <Typography variant="caption" color="success.main">
                                {t('createAccount.copiedLabel')}
                            </Typography>
                        )}

                        <Box sx={createAccountStyles.successActions}>
                            <Button variant="outlined" onClick={() => navigate('/app/admin/users')}>
                                {t('createAccount.backToList')}
                            </Button>
                            <Button variant="contained" onClick={() => {
                                setCopied(false);
                                setInvitationLink(null);
                                setFormData({ firstName: '', lastName: '', email: '', role: 'dealer', dealerId: '' });
                            }}>
                                {t('createAccount.createAnother')}
                            </Button>
                        </Box>
                    </Box>
                </Paper>
            </Box>
        );
    }

    return (
        <Box sx={createAccountStyles.root}>
            <Box sx={createAccountStyles.titleRow}>
                <IconButton component={Link} to="/app/admin/users" size="small">
                    <ArrowLeft size={20} />
                </IconButton>
                <Typography variant="h4" fontWeight={700}>{t('createAccount.title')}</Typography>
            </Box>

            <Paper elevation={0} sx={createAccountStyles.paper}>
                <Box component="form" onSubmit={handleSubmit} sx={createAccountStyles.form}>
                    <FormControl fullWidth>
                        <InputLabel id="create-account-role-label">{t('createAccount.accountTypeLabel')}</InputLabel>
                        <Select
                            id="create-account-role"
                            labelId="create-account-role-label"
                            label={t('createAccount.accountTypeLabel')}
                            value={formData.role}
                            onChange={(e) => setFormData({ ...formData, role: e.target.value as Role })}
                        >
                            <MenuItem value="dealer">{t('createAccount.roleDealer')}</MenuItem>
                            <MenuItem value="admin">{t('createAccount.roleAdmin')}</MenuItem>
                            <MenuItem value="customer">{t('createAccount.roleCustomer')}</MenuItem>
                        </Select>
                    </FormControl>

                    {roleInfo[formData.role] && (
                        <Alert severity={formData.role === 'admin' ? 'warning' : 'info'}>
                            {roleInfo[formData.role]}
                        </Alert>
                    )}

                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <TextField
                                label={t('common.firstName')}
                                required
                                fullWidth
                                value={formData.firstName}
                                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                                autoComplete="given-name"
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                label={t('common.lastName')}
                                required
                                fullWidth
                                value={formData.lastName}
                                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                                autoComplete="family-name"
                            />
                        </Grid>
                    </Grid>

                    <TextField
                        label={t('common.email')}
                        type="email"
                        required
                        fullWidth
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        helperText={t('createAccount.emailHelperText')}
                    />

                    {formData.role === 'dealer' && (
                        <FormControl fullWidth required error={dealerRequiresSelection}>
                            <InputLabel id="create-account-dealer-label">{t('createAccount.dealerLabel')}</InputLabel>
                            <Select
                                id="create-account-dealer"
                                labelId="create-account-dealer-label"
                                label={t('createAccount.dealerLabel')}
                                value={formData.dealerId}
                                onChange={(e) => setFormData({ ...formData, dealerId: e.target.value })}
                                disabled={loadingDealers}
                            >
                                {dealers.map((d) => (
                                    <MenuItem key={d.id} value={d.id}>{d.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}

                    {error && <Alert severity="error">{error}</Alert>}

                    <Box sx={createAccountStyles.formActions}>
                        <Button variant="outlined" onClick={() => navigate('/app/admin/users')} disabled={loading}>
                            {t('createAccount.cancel')}
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={loading || dealerRequiresSelection}
                            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
                        >
                            {loading ? t('createAccount.creating') : t('createAccount.createAndSend')}
                        </Button>
                    </Box>
                </Box>
            </Paper>
        </Box>
    );
};

export default CreateAccount;
